/**
 * Picking the bucket grid to show a pen on. A Sentinel's payload states no
 * interval, but its score timestamps land on the grid it was trained on, so the
 * spacing between them is the interval.
 */
import { ResampleAggregation, ResampleIntervalUnit } from 'types'

export interface BucketGridSetting {
  interval: number
  intervalUnit: ResampleIntervalUnit
  aggregationOp: ResampleAggregation
}

/** What Sentinel training uses when nothing more specific is known. */
export const DEFAULT_BUCKET_GRID: BucketGridSetting = {
  interval: 10,
  intervalUnit: 'minutes',
  aggregationOp: 'avg',
}

const UNIT_SECONDS: Array<{ unit: ResampleIntervalUnit; seconds: number }> = [
  { unit: 'days', seconds: 86400 },
  { unit: 'hours', seconds: 3600 },
  { unit: 'minutes', seconds: 60 },
  { unit: 'seconds', seconds: 1 },
]

/** The coarsest unit that expresses this many seconds without a remainder. */
export const secondsToInterval = (
  seconds: number
): { interval: number; intervalUnit: ResampleIntervalUnit } | null => {
  if (!Number.isFinite(seconds) || seconds < 1) return null

  const whole = Math.round(seconds)
  for (const { unit, seconds: unitSeconds } of UNIT_SECONDS) {
    if (whole >= unitSeconds && whole % unitSeconds === 0) {
      return { interval: whole / unitSeconds, intervalUnit: unit }
    }
  }
  return { interval: whole, intervalUnit: 'seconds' }
}

/**
 * The grid a series of score timestamps sits on, or null if it cannot be told.
 *
 * Median gap rather than mean: a scoring outage leaves one enormous gap, and a
 * mean would report a grid no bucket lands on.
 */
export const inferBucketInterval = (
  timestamps: number[]
): { interval: number; intervalUnit: ResampleIntervalUnit } | null => {
  if (timestamps.length < 3) return null

  const gaps: number[] = []
  for (let index = 1; index < timestamps.length; index += 1) {
    const gap = timestamps[index] - timestamps[index - 1]
    if (gap > 0) gaps.push(gap)
  }
  if (gaps.length === 0) return null

  gaps.sort((left, right) => left - right)
  const median = gaps[Math.floor(gaps.length / 2)]

  return secondsToInterval(median / 1000)
}
